import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Award, Sparkles, TrendingUp, FlaskConical, HardHat,
  ShieldCheck, Info,
} from "lucide-react";
import InfographicHero from "@/components/InfographicHero";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

/**
 * CaseStudiesPage — /case-studies
 * Portfolio + case studies template. Every card is EXPLICITLY labelled either
 *   • "Live client" — real customer, real numbers we can attest to
 *   • "Onboarding pilot" — in progress, illustrative claims disclosed
 *   • "Illustrative template" — no live customer yet; sample layout only
 * per master prompt §16 (never use fabricated data to imply production success).
 */

const STUDIES = [
  {
    kind: "live",
    slug: "qst",
    industry: "Construction training",
    name: "Quality Safety Times",
    summary: "Trade-specific quality + safety training platform. Multilingual lesson library with foreman-first UX.",
    highlights: [
      { label: "Languages published", value: "3", note: "en + hi + ml" },
      { label: "Lesson library", value: "21+", note: "curated per-trade modules" },
      { label: "Platform launch", value: "2026", note: "on WebsiteTech.pro AI" },
    ],
    externalUrl: "/qst",
    accent: "amber",
    icon: HardHat,
  },
  {
    kind: "live",
    slug: "litlab",
    industry: "Metallurgical testing",
    name: "L.I.T. Labs",
    summary: "Nadcap + AC7006 (ISO/IEC 17025) accredited lab · same-week quotes · audit-ready chain-of-custody reports.",
    highlights: [
      { label: "Accreditations shown", value: "Nadcap + AC7006", note: "on-page schema published" },
      { label: "Quote flow", value: "5-category", note: "deep-linked from services" },
      { label: "Design system", value: "Standalone", note: "own navbar + footer + brand" },
    ],
    externalUrl: "/litlab",
    accent: "sky",
    icon: FlaskConical,
  },
  {
    kind: "onboarding",
    slug: "commerce-pilot",
    industry: "Multi-store retail",
    name: "AI Commerce Intelligence pilot",
    summary: "Retail chain pilot syncing prices across 12 storefronts with automated publishing rules. Currently onboarding.",
    highlights: [
      { label: "Stores connected (target)", value: "12", note: "pilot scope" },
      { label: "Publish frequency", value: "Weekly", note: "human-in-the-loop" },
      { label: "Status", value: "Onboarding", note: "public rollout Q4 2026" },
    ],
    externalUrl: "/solutions/ai-commerce-intelligence",
    accent: "emerald",
    icon: Sparkles,
  },
  {
    kind: "template",
    slug: "template-restaurant",
    industry: "Restaurant / Hospitality",
    name: "Restaurant module (illustrative)",
    summary: "Template layout showing what a restaurant client site can achieve with WebsiteTech. No live customer yet — sample metrics.",
    highlights: [
      { label: "Menu items", value: "sample", note: "illustrative only" },
      { label: "Booking channels", value: "web + WhatsApp", note: "illustrative flow" },
      { label: "Multilingual", value: "en + local", note: "sample deployment" },
    ],
    externalUrl: "/solutions/restaurants",
    accent: "orange",
    icon: TrendingUp,
    illustrative: true,
  },
  {
    kind: "template",
    slug: "template-dental",
    industry: "Dental / Medical",
    name: "Dental clinic module (illustrative)",
    summary: "Template layout — appointment intake + reminders + multilingual patient assistance. No live customer yet.",
    highlights: [
      { label: "Appointment types", value: "sample", note: "illustrative only" },
      { label: "Consent workflow", value: "yes", note: "consent-first design" },
      { label: "PII policy", value: "no LLM", note: "sensitive fields sandboxed" },
    ],
    externalUrl: "/solutions/healthcare",
    accent: "violet",
    icon: TrendingUp,
    illustrative: true,
  },
];

const ACC = {
  amber: "border-amber-200 bg-amber-50/40 hover:border-amber-300",
  sky: "border-sky-200 bg-sky-50/40 hover:border-sky-300",
  emerald: "border-emerald-200 bg-emerald-50/40 hover:border-emerald-300",
  orange: "border-orange-200 bg-orange-50/40 hover:border-orange-300",
  violet: "border-violet-200 bg-violet-50/40 hover:border-violet-300",
};
const KIND_BADGE = {
  live: { label: "Live client", tone: "bg-emerald-500 text-white" },
  onboarding: { label: "Onboarding pilot", tone: "bg-amber-500 text-white" },
  template: { label: "Illustrative template", tone: "bg-slate-400 text-white" },
};

export default function CaseStudiesPage() {
  usePageSeo(PAGE_SEO["/case-studies"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Results", url: "/case-studies" }, { name: "Case Studies", url: "/case-studies" }]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="case-studies-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Award className="h-4 w-4" /> Case Studies · Portfolio
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Every case labelled. <span className="text-orange-600">Nothing invented.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-slate-600 sm:text-lg">
            We show live clients, onboarding pilots, and illustrative
            industry templates — each one clearly tagged so you know what's
            real production and what's a template you'd need to fill in.
          </p>
          <div className="mt-6">
            <InfographicHero slug="complete-platform" hint={false} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600" data-testid="case-studies-legend">
          <Info className="h-4 w-4 text-slate-400" />
          Label key:
          <Badge className="bg-emerald-500 text-white">Live client</Badge> real production ·
          <Badge className="bg-amber-500 text-white">Onboarding pilot</Badge> in progress, disclosed ·
          <Badge className="bg-slate-400 text-white">Illustrative template</Badge> no live customer yet, sample only
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {STUDIES.map((s) => {
            const Icon = s.icon;
            const badge = KIND_BADGE[s.kind];
            return (
              <Link
                key={s.slug}
                to={s.externalUrl}
                className={`group block rounded-2xl border-2 p-6 transition ${ACC[s.accent]} hover:-translate-y-0.5 hover:shadow-xl`}
                data-testid={`case-study-${s.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
                      <Icon className="h-6 w-6 text-slate-700" />
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                        {s.industry}
                      </div>
                      <div className="mt-0.5 font-heading text-xl font-black text-slate-900">
                        {s.name}
                      </div>
                    </div>
                  </div>
                  <Badge className={badge.tone}>{badge.label}</Badge>
                </div>
                <p className="mt-3 text-sm text-slate-700">{s.summary}</p>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {s.highlights.map((h) => (
                    <div key={h.label} className="rounded-lg border border-slate-200 bg-white p-2">
                      <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                        {h.label}
                      </div>
                      <div className="mt-0.5 font-heading text-lg font-black text-slate-900">
                        {h.value}
                      </div>
                      <div className="text-[10px] text-slate-500">{h.note}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  {s.illustrative && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      Metrics are illustrative
                    </span>
                  )}
                  <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-orange-600 group-hover:gap-2">
                    Open <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Trust reminders */}
      <section className="border-y border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-sky-50">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-12 md:grid-cols-3">
          <TrustCard icon={ShieldCheck} title="Milestone guarantee"
            body="If we do not complete the agreed technical, local-listing, content and conversion milestones within the onboarding period, we will continue the affected managed service without charge until they are completed." />
          <TrustCard icon={Award} title="You own everything"
            body="Your domain, content, translations, imagery, customer data and analytics — exportable at any time, with no lock-in." />
          <TrustCard icon={TrendingUp} title="Named human strategist"
            body="Every managed plan ships with a named success manager alongside the AI. See the identity page to view yours." />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-2xl font-black text-slate-900">Want to be case study #4?</h2>
              <p className="mt-1 text-sm text-slate-600">Run a free Local Visibility audit and we'll show you the growth plan first.</p>
            </div>
            <div className="flex gap-2">
              <Button asChild className="bg-orange-600 text-white hover:bg-orange-700">
                <Link to="/local-visibility">Run free audit <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/packages">See packages</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white bg-white/80 p-6 shadow-sm backdrop-blur">
      <Icon className="h-6 w-6 text-orange-600" />
      <div className="mt-3 font-heading text-lg font-bold text-slate-900">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-slate-700">{body}</div>
    </div>
  );
}
